import {ChangeDetectorRef, Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router, UrlSegment} from '@angular/router';
import {CommonMessagesService} from '../../../services/common-messages.service';
import {Sequence} from '../../../model/sequence';
import {SequencesService} from '../../../services/sequences.service';
import {SequencePersistRequest} from '../../../model/requests/sequence-persist-request';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {SequenceFormInfoComponent} from './sequences-form-info/sequence-form-info.component';
import {ComponentCanDeactivate} from '../../../common/can-deactivate/component-can-deactivate';
import {TenantsService} from '../../../services/tenants.service';
import {Tenant} from '../../../model/tenant';
import {AuthGuard} from '../../../common/auth-guard.service';
import {AssetLoaderService} from '../../../services/asset-loader.service';


@Component({
  selector: 'app-sequence-form',
  templateUrl: './sequence-form.component.html',
  styleUrls: ['./sequence-form.component.scss']
})
export class SequenceFormComponent implements OnInit, ComponentCanDeactivate {
  public mode: 'create' | 'edit' | undefined;

  public sequence: Sequence;
  public persistingChanges: any;

  public items: any[];
  public currentStep: number;

  public formLoaded: boolean;
  public formSuccess: boolean;
  public stepChanged: boolean;

  public sequencePersistRequest: SequencePersistRequest;

  public tenants: Tenant[];

  public errors: { property: string, message: string } [];

  public visible = false;

  public asset: any;
  public sequenceSchema = 'form-config/sequences/sequence-schema.json';

  @ViewChild(SequenceFormInfoComponent)
  private sequenceFormInfoComponent: SequenceFormInfoComponent;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly commonMessagesService: CommonMessagesService,
    private readonly sequencesService: SequencesService,
    private readonly cdr: ChangeDetectorRef,
    private readonly tenantsService: TenantsService,
    private readonly authGuard: AuthGuard,
    private readonly assetLoaderService: AssetLoaderService
  ) {
    this.sequencePersistRequest = new SequencePersistRequest();
    this.errors = [];
  }

  ngOnInit(): void {
    this.currentStep = 0;
    this.items = [
      {index: 0, icon: 'pi pi-hourglass', label: 'model.sequence', description: 'forms.descriptions.sequence'},
      {
        index: 1,
        icon: 'pi pi-cog',
        label: 'forms.configuration',
        description: 'forms.descriptions.sequenceConfiguration'
      },
      {index: 2, icon: 'pi pi-check', label: 'forms.confirmation'}
    ];

    this.route.url.subscribe((urlSegment: UrlSegment[]): void => {
      const firstPath: string = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }


  public persistSequenceData(): void {
    this.persistingChanges = true;
    this.errors = [];

    let promise: Promise<any>;
    if (this.mode === 'create') {
      promise = this.sequencesService.createSequence(this.sequencePersistRequest).toPromise();
    } else {
      promise = this.sequencesService.updateSequence(this.sequencePersistRequest).toPromise();
    }

    promise
      .then((): void => {
        this.formSuccess = true;
        this.router.navigate([`/sequences`]);
      })
      .catch((httpErrorResponse: HttpErrorResponse): void => {
        if (httpErrorResponse.error?.errorValidationList) {
          this.errors = httpErrorResponse.error?.errorValidationList;
          this.commonMessagesService.showSequenceErrorConfigurationInvalidMessage(this.mode);
        } else {
          this.commonMessagesService.showPersistingError(this.mode, httpErrorResponse, this.sequencePersistRequest.name);
        }
        this.persistingChanges = false;
      });
  }

  public onNextStep(): void {
    this.stepChanged = true;
    this.currentStep++;
  }

  public onPreviousStep(): void {
    this.stepChanged = true;
    if (this.currentStep > 0) {
      this.currentStep--;
      if (this.currentStep === 0) {
        this.cdr.detectChanges();
        this.sequenceFormInfoComponent.loadFormComponent();
      }
    }
  }

  public onCancel(route: string): Promise<boolean> {
    if (route == null) {
      return this.router.navigate([`/sequences`]);
    }
    return this.router.navigate([`${route}`]);
  }

  private loadCreationForm(): void {
    this.sequence = new Sequence();

    const promises: Promise<any>[] = [];
    promises.push(this.tenantsService.getTenantSummaries().toPromise());
    promises.push(this.assetLoaderService.loadAssetWithRoute(this.sequenceSchema).toPromise());

    Promise.all(promises)
      .then((response: [ {tenants: [Tenant]}, any]): void => {
        this.tenants = response[0].tenants;
        if (!this.authGuard.superAdminRequired()) {
          this.sequence.tenant = this.tenants[0];
        }
        this.asset = response[1];
      })
      .finally((): void => {
        this.formLoaded = true;
        this.sequenceFormInfoComponent.loadFormComponent();
      });
  }

  private loadEditionForm(uuid: string): void {
    const promises: Promise<any>[] = [];
    promises.push(this.sequencesService.getSequence(uuid).toPromise());
    promises.push(this.assetLoaderService.loadAssetWithRoute(this.sequenceSchema).toPromise());

    Promise.all(promises)
      .then((response: [{ sequence: Sequence }, any]): void => {
        this.sequence = response[0].sequence;
        this.asset = response[1];
        this.asset.template = this.sequence.configuration;
        this.tenants = [this.sequence.tenant];
        this.cdr.detectChanges();
      })
      .catch((error: HttpErrorResponse): void => {
        if (error.status === HttpStatusCode.NotFound) {
          this.formSuccess = true;
          this.router.navigate(['/not-found']);
        }
      })
      .finally((): void => {
        this.formLoaded = true;
        this.sequenceFormInfoComponent.loadFormComponent();
      });
  }

  @HostListener('window:beforeunload')
  public canDeactivate(): boolean {
    return this.formSuccess || (this.currentStep === 0 && (this.sequenceFormInfoComponent?.sequenceForm?.untouched)) || this.stepChanged;
  }

}
